<template>
  <div ref="containerRef" class="multi-select-combobox">
    <div class="selected-items" :class="{ 'has-items': modelValue.length > 0 }">
      <template v-for="item in selectedItems" :key="getItemValue(item)">
        <slot
          name="selected-item"
          :item="item"
          :label="getItemLabel(item)"
          :value="getItemValue(item)"
          :remove="() => remove(getItemValue(item))"
        >
          <span class="tag is-rounded is-light">
            {{ getItemLabel(item) }}
            <button class="delete is-small" type="button" @click="remove(getItemValue(item))" />
          </span>
        </slot>
      </template>

      <div class="control has-icons-left is-flex-grow-1">
        <input
          ref="inputRef"
          v-model="searchText"
          class="input"
          type="search"
          :placeholder="modelValue.length === 0 ? placeholder : ''"
          autocomplete="off"
          @focus="open"
          @input="open"
          @blur="handleBlur"
          @keydown="handleKeydown"
          @keydown.backspace="handleBackspace"
        />
      </div>
    </div>

    <div v-show="isOpen && filteredItems.length > 0" ref="listRef" class="combobox-dropdown">
      <div
        v-for="(item, index) in filteredItems"
        :key="getItemValue(item)"
        :ref="(el) => setItemRef(el, index)"
        class="dropdown-item"
        :class="{ 'is-active': activeIndex === index }"
        role="option"
        @pointerdown.prevent="select(item)"
        @mouseenter="setActiveIndex(index)"
      >
        {{ getItemLabel(item) }}
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";
  import { useCombobox } from "../lib/use-combobox";
  import { useComboboxBlur } from "../lib/use-combobox-blur";

  const props = defineProps({
    modelValue: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    labelKey: { type: String, default: "label" },
    valueKey: { type: String, default: "value" },
    placeholder: { type: String, default: "Select..." },
    keepOpenOnSelect: { type: Boolean, default: true },
  });

  const emit = defineEmits(["update:model-value"]);

  const searchText = ref("");
  const inputRef = ref(null);

  function getItemLabel(item) {
    return item[props.labelKey];
  }

  function getItemValue(item) {
    return item[props.valueKey];
  }

  const selectedItems = computed(() =>
    props.modelValue
      .map((val) => props.items.find((item) => getItemValue(item) === val))
      .filter(Boolean)
  );

  const filteredItems = computed(() => {
    const query = searchText.value.trim().toLowerCase();
    return props.items.filter((item) => {
      if (props.modelValue.includes(getItemValue(item))) return false;
      if (!query) return true;
      return getItemLabel(item).toLowerCase().includes(query);
    });
  });

  const {
    isOpen,
    activeIndex,
    containerRef,
    listRef,
    open,
    close,
    setActiveIndex,
    setItemRef,
    handleKeydown,
  } = useCombobox({
    items: filteredItems,
    onSelect: (item) => select(item),
    keepOpenOnSelect: props.keepOpenOnSelect,
  });

  const { handleBlur } = useComboboxBlur({ containerRef, close });

  function select(item) {
    emit("update:model-value", [...props.modelValue, getItemValue(item)]);
    searchText.value = "";
    inputRef.value?.focus();
  }

  function remove(value) {
    emit(
      "update:model-value",
      props.modelValue.filter((v) => v !== value)
    );
  }

  function handleBackspace() {
    if (searchText.value === "" && props.modelValue.length > 0) {
      remove(props.modelValue[props.modelValue.length - 1]);
    }
  }
</script>

<style scoped lang="scss">
  @import "~jose_modern/css";

  .multi-select-combobox {
    position: relative;
  }

  .selected-items {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
    min-height: 1.5em;
    padding: 0.1rem 0.2rem;
    border: 1px solid $border;
    border-radius: $radius;
    background: $white;
    cursor: text;

    &:focus-within {
      border-color: $link;
      box-shadow: 0 0 0 0.125em rgba($link, 0.25);
    }

    .tag {
      margin: 0;
    }

    .control {
      min-width: 8rem;
      flex: 1;

      .input {
        border: none;
        box-shadow: none;
        padding-left: 0.5rem;
        height: auto;
        min-height: unset;

        &:focus {
          box-shadow: none;
        }
      }
    }
  }

  .combobox-dropdown {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    z-index: 100;
    background: $white;
    border: 1px solid $border;
    border-radius: $radius;
    box-shadow: $box-shadow;
    max-height: 200px;
    overflow-y: auto;

    .dropdown-item {
      padding: 0.4rem 0.75rem;
      cursor: pointer;

      &.is-active,
      &:hover {
        background-color: $link;
        color: $white;
      }
    }
  }
</style>
